/**
 * Outreach v2 — Type Definitions
 *
 * Mirrors the backend outreach entities (apps/server/modules/outreach/) so the
 * frontend speaks the same shape the API returns. Enums are kept in lockstep
 * with `outreach.constants.js`; the safe-default values live in `./constants.ts`.
 *
 * Talent Outreach Agent spec §3 (entities), §11 (caps), §12 (compliance).
 * v2 module — Lantern tokens only, no v1 shadcn imports.
 */

/* ─── Accounts (§3, §11) ─────────────────────────────────── */

export type AccountProvider = 'gmail' | 'outlook' | 'imap' | 'linkedin' | 'whatsapp';
export type AccountChannel = 'email' | 'linkedin' | 'whatsapp';
export type AccountStatus =
    | 'pending'
    | 'connected'
    | 'warming'
    | 'limited'
    | 'disconnected'
    | 'error'
    // This identity+channel is actively bound in ANOTHER workspace — parked until
    // the owner claims it ("Disconnect from all other organizations"). Not sendable,
    // ignores inbound (Stage 1).
    | 'on_hold';

/** Per-account safety caps (§11). minDelaySeconds + jitter define the
 *  randomized gap between sends (e.g. 45s base + up to 75s jitter ≈ 45–120s). */
export type AccountCaps = {
    daily: number;
    hourly: number;
    minDelaySeconds: number;
    jitter: number;
};

/** Send window — `days` are 0 (Sunday) … 6 (Saturday); start/end are 'HH:mm'
 *  local to `tz`. */
export type SendWindow = {
    tz: string;
    days: number[];
    start: string;
    end: string;
};

export type AccountCapabilities = {
    send: boolean;
    receive: boolean;
    invite: boolean;
};

export type LinkedInFeatures = {
    premium: boolean | null;
    openProfile: boolean | null;
    recruiterContract: unknown;
    salesNavContract: unknown;
    inmailBalance: {
        premium: number | null;
        recruiter: number | null;
        sales_navigator: number | null;
    };
    checkedAt: string | null;
};

export type OutreachAccount = {
    _id: string;
    organizationId: string;
    ownerId: string;
    provider: AccountProvider;
    channel: AccountChannel;
    unipileAccountId: string | null;
    displayName?: string;
    emailAddress?: string;
    status: AccountStatus;
    capabilities: AccountCapabilities;
    caps: AccountCaps;
    /** LinkedIn invite caps — lower limits than messages (§11). Null for email. */
    inviteCaps: AccountCaps | null;
    /** LinkedIn InMail caps — independent from messages/invites. Null outside LinkedIn. */
    inmailCaps: AccountCaps | null;
    /** Warm-up-ramped caps the engine actually enforces today (server-computed
     *  in listAccounts) — render THESE as the usage denominator so the first
     *  paint matches what analytics later returns (no 80→24 flicker). */
    effectiveCaps?: AccountCaps;
    effectiveInviteCaps?: AccountCaps;
    effectiveInmailCaps?: AccountCaps;
    /** Provider-confirmed LinkedIn seat types and current InMail credit balances. */
    linkedinFeatures?: LinkedInFeatures | null;
    sendWindow: SendWindow;
    /** Bypass the business-hours `sendWindow` — send at any time (caps still apply). */
    sendAnytime?: boolean;
    signature: string;
    warmupStartedAt: string | null;
    lastActivityAt: string | null;
    lastError: string | null;
    /** Normalized provider-confirmed identity (`email:…`|`linkedin:…`|`whatsapp:…`),
     *  used for the one-active-binding-per-identity rule (Stage 1). */
    identityKey?: string | null;
    /** True IFF this row holds the single active binding for its identity+channel. */
    claimActive?: boolean;
    createdAt: string;
    updatedAt: string;
    /** Optional today's-usage rollup attached by the analytics endpoint (§13).
     *  Not part of the base account document — present when usage is requested. */
    usage?: AccountUsage;
    /** Client-only: the hosted-auth URL for a `pending` connection, stashed when
     *  the connect call returns so the card can offer "Finish connecting". Not
     *  persisted — gone after a reload (the row is still cancellable + GC'd). */
    hostedAuthUrl?: string;
};

/** Today's send counts vs caps for the per-account usage meter (§13). */
export type AccountUsage = {
    sentToday: number;
    dailyCap: number;
    sentThisHour: number;
    hourlyCap: number;
    invitesSentToday?: number;
    inviteDailyCap?: number;
    inmailsSentToday?: number;
    inmailDailyCap?: number;
    lastSlotAt?: string | null;
};

export type ConnectAccountPayload = {
    provider: AccountProvider;
    channel?: AccountChannel;
    displayName?: string;
    emailAddress?: string;
    /** Browser-resolved IANA timezone used for the account's initial send window. */
    timezone?: string;
};

/** Response from POST /accounts/connect. In simulated mode (no Unipile creds)
 *  the account comes back already `connected` and `hostedAuthUrl` is null. */
export type ConnectAccountResult = {
    account: OutreachAccount;
    hostedAuthUrl: string | null;
    simulated: boolean;
    /** Set when re-adding an already-connected account of your own (no-op). */
    alreadyConnected?: boolean;
    /** Set when this connect revived an existing row in place (reconnect). */
    reconnect?: boolean;
    /** Set when the account landed `on_hold` (held by another workspace). */
    onHold?: boolean;
};

/** Response from POST /accounts/:id/claim (Stage 1 takeover). */
export type ClaimAccountResult = {
    account: OutreachAccount;
    claimed: boolean;
    /** How many other-org/other bindings were disconnected by the claim. */
    disconnected?: number;
    /** Fresh hosted-auth URL — a claim ends with ONE clean re-sign-in, because
     *  deleting any duplicate connection revokes the shared Google OAuth grant,
     *  so no pre-existing connection can survive the takeover. */
    hostedAuthUrl?: string | null;
    /** True when the hosted-auth re-sign-in is required to finish the claim. */
    reauthRequired?: boolean;
};

export type UpdateAccountPayload = {
    caps?: Partial<AccountCaps>;
    inviteCaps?: Partial<AccountCaps>;
    inmailCaps?: Partial<AccountCaps>;
    sendWindow?: Partial<SendWindow>;
    sendAnytime?: boolean;
    displayName?: string;
    signature?: string;
};

/* ─── Campaign (§3) ──────────────────────────────────────── */

export type CampaignGoal =
    | 'book_call'
    | 'invite_apply'
    | 'request_cv'
    | 'confirm_interest'
    | 'interview_invite'
    | 'start_qualification'
    | 'talent_pool';
export type CampaignStatus = 'draft' | 'ready' | 'active' | 'paused' | 'completed' | 'archived';
/** Trimmed to what's implemented (H12) — csv/linkedin import never shipped. */
export type AudienceSource = 'db';
export type ApprovalMode = 'sample' | 'per_message';

export type CampaignAudience = {
    source: AudienceSource;
    candidateIds: string[];
    filters: Record<string, unknown>;
};

export type CampaignApproval = {
    mode: ApprovalMode;
    sampleSize: number;
    sampleApproved: boolean;
    approvedBy: string | null;
    approvedAt: string | null;
};

export type OutreachCampaign = {
    _id: string;
    organizationId: string;
    workspaceId?: string;
    /** Null for a standalone (CSV-audience) campaign; set when linked to a job. */
    jobId: string | null;
    ownerId: string;
    name: string;
    goal: CampaignGoal;
    status: CampaignStatus;
    audience: CampaignAudience;
    sequenceId: string | null;
    accountIds: string[];
    approval: CampaignApproval;
    bookingLink: string;
    tone: string;
    /** Optional audience brief that seeds the AI draft for standalone campaigns. */
    audienceBrief?: string;
    allowDoubleEnroll: boolean;
    launchedAt: string | null;
    launchedBy: string | null;
    /** Live prospect count (Stage 2) — the server computes it per list row. */
    audienceCount?: number;
    /** Composable data sources attached to the campaign (Stage 2, P2). */
    sources?: CampaignSource[];
    createdAt: string;
    updatedAt: string;
};

export type CreateCampaignPayload = {
    /** Optional — omit for a standalone campaign (audience comes from a CSV). */
    jobId?: string;
    name: string;
    goal?: CampaignGoal;
    audience?: Partial<CampaignAudience>;
    accountIds?: string[];
    bookingLink?: string;
    tone?: string;
    audienceBrief?: string;
};

export type ListCampaignsParams = {
    jobId?: string;
    status?: CampaignStatus;
    ownerId?: string;
};

/* ─── CSV contact import (standalone audience) ───────────────── */

/** One row of a CSV import after the recruiter maps columns. */
export type ImportContactRow = {
    name?: string;
    email?: string;
    linkedinUrl?: string;
    company?: string;
    title?: string;
    phone?: string;
};

/** A People row returned by import-contacts, ready to enrich + enroll. */
export type ImportedContact = {
    personId: string;
    name: string;
    title: string;
    company: string;
    hasEmail: boolean;
    hasLinkedIn: boolean;
    hasPhone: boolean;
};

export type ImportContactsResult = {
    /** Newly created People. */
    imported: number;
    /** Rows that matched an existing Person (deduped). */
    matched: number;
    total: number;
    contacts: ImportedContact[];
    personIds: string[];
    /** Subset with a LinkedIn URL but no email — feed to the enrichment waterfall. */
    missingEmailPersonIds: string[];
};

/* ─── Campaign data sources (Stage 2, P2) ─────────────────── *
 * A campaign owns composable sources: a CSV upload, a pool pick, a one-off
 * search push, or a scheduled search. Every add writes an OutreachSourceRun;
 * `counts` is the intake-time snapshot, `live` is recomputed per request from
 * the prospects carrying that runId. */

export type CampaignSourceType = 'csv' | 'pool' | 'search_push' | 'search_scheduled';

/** auto = intake + enroll in one go; review = prospects park at stage
 *  'sourced' until a human promotes picks (the sourced-table checkboxes). */
export type SourceEnrollMode = 'auto' | 'review';

export type CampaignSourceSchedule = {
    enabled: boolean;
    intervalHours: number;
    nextDueAt: string | null;
    lastRunAt: string | null;
};

export type CampaignSource = {
    _id: string;
    type: CampaignSourceType;
    label: string;
    /** Per-type config: { sourcingConfigId } (search), { fileName, rowCount }
     *  (csv), { filters } (pool). */
    config: {
        sourcingConfigId?: string;
        fileName?: string;
        rowCount?: number;
        filters?: Record<string, unknown>;
    };
    enrollMode: SourceEnrollMode;
    schedule?: CampaignSourceSchedule;
    createdBy: string | null;
    createdAt?: string;
};

export type SourceRunStatus = 'running' | 'completed' | 'failed';

/** Intake-time snapshot of what one run did. */
export type SourceRunCounts = {
    scanned: number;
    new: number;
    duplicate: number;
    suppressed: number;
    skipped: number;
    enriched: number;
    contactable: number;
    enrolled: number;
    held: number;
};

/** Live prospect counts by runId — a review-mode prospect promoted days later
 *  shows up here, not in `counts`. */
export type SourceRunLive = {
    total: number;
    sourced: number;
    held: number;
    enrolled: number;
};

export type SourceRun = {
    _id: string;
    sourceId: string | null;
    sourceType: string;
    trigger: string;
    status: SourceRunStatus;
    startedAt: string;
    finishedAt: string | null;
    error: string | null;
    counts: SourceRunCounts;
    live?: SourceRunLive;
    createdAt: string;
};

/** One row the enroll/intake path skipped, with a stable reason code. */
export type SkippedEntry = { candidateId: string; reason: string };

/** Recruiter-friendly copy for the skip-reason codes (enroll + intake). */
export const SKIP_REASON_LABELS: Record<string, string> = {
    already_enrolled: 'already in this campaign',
    active_in_another_campaign: 'active in another campaign',
    candidate_not_found: 'no longer in the talent pool',
    no_contactable_channel: 'no email or LinkedIn on file',
    invalid_id: 'invalid id',
    no_identity: 'no identity data',
    suppressed: 'suppressed (do-not-contact)',
};

export type CreateSourcePayload = {
    type: 'csv' | 'search_push' | 'search_scheduled';
    label?: string;
    enrollMode?: SourceEnrollMode;
    config?: { sourcingConfigId?: string };
    /** CSV source: client-parsed rows. */
    rows?: ImportContactRow[];
    /** Search push: narrow to a manual shortlist selection. */
    candidateKeys?: string[];
    schedule?: { intervalHours?: number };
    /** search_scheduled only: also execute a push in the same call. */
    runNow?: boolean;
};

export type UpdateSourcePayload = {
    label?: string;
    enrollMode?: SourceEnrollMode;
    schedule?: { enabled?: boolean; intervalHours?: number };
};

/** POST /sources | /sources/:id/run — what the run did. `enrolled` refs are
 *  candidateKeys (search) / csv-row:N (csv). `sourcedCount` = intaken but NOT
 *  enrolled (review mode → awaiting promotion). `noLinkedin` (search only) =
 *  shortlist rows with no LinkedIn URL, filtered before intake. */
export type SourceRunResult = {
    source?: CampaignSource;
    run?: SourceRun;
    enrolled?: string[];
    held?: string[];
    skipped?: SkippedEntry[];
    noLinkedin?: number;
    heldCount?: number;
    sourcedCount?: number;
};

/** POST /prospects/enroll (promote) — the manual review gate's result.
 *  `enrolledProspectIds` are the ones that actually started; `held` are rows
 *  parked for lack of a reachable channel; `skipped` carries a reason each
 *  (e.g. active_in_another_campaign, suppressed). All three can be non-empty
 *  even on a 200 — the caller must inspect them, not assume success. */
export type PromoteResult = {
    enrolled: string[];
    enrolledProspectIds: string[];
    held?: string[];
    skipped: SkippedEntry[];
};

/** One search-v3 config of the current user (GET /search-sources). */
export type SearchSourceSummary = {
    configId: string;
    name: string;
    status: string;
    dailyTarget?: number;
    lastRunAt: string | null;
    total: number;
    pushable: number;
    contacted: number;
};

/** One pushable shortlist row (GET /search-sources/:configId/candidates). */
export type SearchSourceCandidate = {
    candidateKey: string;
    name: string;
    title: string;
    company: string;
    location: string;
    linkedinUrl: string;
    avatarUrl: string | null;
    matchRate: number | null;
    status: string;
    deliveredAt: string | null;
};

/* ─── Sequence + Step/node (§3, §5) ──────────────────────── */

export type SequenceStatus = 'draft' | 'active' | 'archived';
export type StepType =
    | 'email'
    | 'linkedin_invite'
    | 'linkedin_message'
    | 'linkedin_inmail'
    | 'whatsapp_message'
    | 'manual_task'
    | 'branch';
export type StepChannel = 'email' | 'linkedin' | 'whatsapp';
export type AiMode = 'manual' | 'template' | 'ai_draft' | 'ai_autopilot';
export type DelayUnit = 'minutes' | 'hours' | 'days';
export type PersonalizationMode = 'none' | 'light' | 'deep';
export type ConditionType =
    | 'hasEmail'
    | 'hasLinkedIn'
    | 'hasPhone'
    | 'inviteAccepted'
    | 'repliedAnyChannel'
    | 'emailBounced'
    | 'unsubscribed'
    | 'taskCompleted'
    | 'opened'
    | 'clicked'
    | 'markedInterested'
    | 'markedMaybe'
    | 'markedNotInterested';
export type ConditionTimingMode = 'wait_until' | 'within';
export type FallbackType =
    | 'none'
    | 'email'
    | 'linkedin_invite'
    | 'linkedin_message'
    | 'linkedin_inmail'
    | 'whatsapp_message'
    | 'skip';

export type StepDelay = { value: number; unit: DelayUnit };

/** Config for one advanced `{{ai2:<id>}}` part: the recruiter's instructions
 *  plus example passages that show the AI the length/tone to match. Generated
 *  PER PROSPECT at send/preview time (sourcing profile included when known). */
export type AiPartConfig = {
    instructions: string;
    examples: string[];
};

export type StepMessage = {
    subject: string;
    body: string;
    inviteNote: string;
    /** Recruiter's pasted prompt guiding what AI writes into the step's
     *  `{{ai}}` blocks (§7 constructor). Empty when the step has no AI blocks. */
    aiPrompt?: string;
    /** Advanced AI parts, keyed by the `{{ai2:<id>}}` tokens in the copy.
     *  Pruned server-side to referenced ids on every sequence save. */
    aiParts?: Record<string, AiPartConfig>;
    personalizationMode: PersonalizationMode;
    variables: string[];
    unsubscribeFooter: boolean;
    requiresConnected: boolean;
    /** linkedin_message: where to route when the connection isn't accepted and
     *  the message can't be delivered (§5/§6). Engine default → email. */
    fallbackIfNotAccepted?: FallbackType;
};

export type StepCondition = {
    type: ConditionType;
    timing: { mode: ConditionTimingMode; windowDays: number };
};

/** manual_task node fields (§5). Present only on `manual_task` nodes. */
export type StepTask = {
    title: string;
    instructions: string;
    assignedUserId: string | null;
    dueOffsetDays: number;
    blocksSequence: boolean;
};

/** linkedin_invite node fields (§5). Present only on `linkedin_invite` nodes. */
export type StepInvite = {
    maxWaitForAcceptanceDays: number;
    fallbackIfMissing: FallbackType;
    fallbackIfConnected: FallbackType;
    fallbackIfNotAccepted: FallbackType;
};

export type StepNode = {
    id: string;
    type: StepType;
    channel: StepChannel | null;
    delay: StepDelay;
    requiredFields: string[];
    accountSelection: { mode: 'auto' | 'specific'; accountId: string | null };
    message: StepMessage;
    aiMode: AiMode;
    approvalRequired: boolean;
    condition: StepCondition | null;
    branches: { yes: string | null; no: string | null };
    /** manual_task-only — null on every other node type. */
    task?: StepTask | null;
    /** linkedin_invite-only — null on every other node type. */
    invite?: StepInvite | null;
    fallback: { type: FallbackType; targetNodeId: string | null };
    position: { x: number; y: number };
};

export type SequenceEdge = {
    id: string;
    source: string;
    target: string;
    branch: 'yes' | 'no' | null;
};

export type OutreachSequence = {
    _id: string;
    organizationId: string;
    campaignId: string | null;
    version: number;
    status: SequenceStatus;
    nodes: StepNode[];
    edges: SequenceEdge[];
    entryNodeId: string | null;
    isTemplate: boolean;
    templateName: string;
    createdAt: string;
    updatedAt: string;
};

/* ─── Prospect (§3, Stage 2 — replaces Enrollment) ───────── */

export type EnrollmentStatus = 'active' | 'paused' | 'stopped' | 'completed' | 'failed';
export type StopReason =
    | 'replied'
    | 'unsubscribed'
    | 'manual'
    | 'marked_interested'
    | 'marked_not_interested'
    | 'meeting_booked'
    | 'entered_pipeline'
    | 'bounced'
    | 'failed';

/** Funnel/kanban position — distinct from the engine `status` (which stays
 *  null until the prospect is actually enrolled). `paused` keeps stage 'active'. */
export type ProspectStage =
    | 'sourced'
    | 'enriching'
    | 'held'
    | 'active'
    | 'replied'
    | 'stopped'
    | 'completed'
    | 'unsubscribed';

/** Denormalized contact identity carried on the prospect (supersedes the old
 *  contactSnapshot). `emails`/`phones` are canonical, preferred FIRST —
 *  emails[0] is the send target. */
export type ProspectIdentity = {
    name: string;
    title: string;
    company: string;
    location: string;
    emails: string[];
    phones: string[];
    linkedinUrl: string | null;
    candidateKey: string | null;
    avatarUrl: string | null;
    websiteUrl: string | null;
};

/** Where the prospect came from (source run provenance). */
export type ProspectSource = {
    sourceId: string | null;
    type: string;
    runId: string | null;
    externalRef: string | null;
    addedAt: string;
};

/** One funnel transition (intake / promote / manual events — not engine ticks). */
export type ProspectStageEvent = {
    stage: ProspectStage;
    at: string;
    reason: string | null;
};

export type OutreachProspect = {
    _id: string;
    organizationId: string;
    campaignId: string;
    /** Org-scoped stable HUMAN key (rides the legacy candidateId slots). */
    personKey: string;
    identity: ProspectIdentity;
    /** Prefixed canonical identity values (email:/li:/phone:/phone9:/ck:/nc:). */
    identityHashes: string[];
    source: ProspectSource;
    stage: ProspectStage;
    stageHistory: ProspectStageEvent[];
    heldReason: string | null;
    heldUntil: string | null;
    enrichAttempts: number;
    /** Durable campaign email-finding lease; queued/running rows are read-only. */
    enrichmentBatch?: {
        operationId: string;
        childJobId?: string | null;
        state: 'queued' | 'running';
        queuedAt?: string | null;
    } | null;
    /** Engine execution state — null = not enrolled yet. */
    status: EnrollmentStatus | null;
    currentNodeId: string | null;
    nextRunAt: string | null;
    stopReason: StopReason | null;
    pauseReason: string | null;
    assignedUserId: string | null;
    /** Waterfall provenance persisted on the row (Stage 3 §4). Only the fields
     *  surfaces render are typed; identity.emails stays the send target. */
    enrichment?: ProspectEnrichment | null;
    /** Compact sequence progress for the roster (attached by the list endpoint
     *  for ENROLLED rows only; null/absent otherwise). See ProspectRowFlow. */
    flow?: ProspectRowFlow | null;
    /** At least one per-step message for this prospect was deliberately edited
     *  by a recruiter. Attached by the list endpoint for the campaign badge. */
    hasEditedPrewrite?: boolean;
    /** Attached by the list endpoint on not-yet-enrolled rows the enroll guard
     *  would BLOCK (e.g. 'active_in_another_campaign') — so the roster shows an
     *  honest status instead of a false "Ready". A SKIP_REASON_LABELS key. */
    enrollBlock?: string | null;
    createdAt: string;
    updatedAt: string;
};

/** Waterfall provenance persisted on the prospect doc (Stage 3 §4). */
export type ProspectEnrichment = {
    status?: EnrichmentStatus | null;
    provider?: string | null;
    foundAt?: string | null;
    lastAttemptAt?: string | null;
    attempts?: number;
    personalEmail?: EnrichmentEmailSlot | null;
    businessEmail?: EnrichmentEmailSlot | null;
    emailVerification?: (EmailVerification & { verifiedAt?: string | null }) | null;
    /** Cooldown gate for re-runs after a not_found. */
    refreshableAfter?: string | null;
    /** Set to 'duplicate_of_sibling' when the waterfall found an email that
     *  already belongs to ANOTHER prospect in this campaign (E11000 append skip):
     *  the identity is left untouched (no send target) and the row derives to the
     *  Duplicated status. */
    appendOutcome?: string | null;
};

/* ─── job-workspace campaign anchor (Stage 3) ─── */

/** Roster stage counts for the anchor campaign — ONE server aggregate, so the
 *  workspace's bucket bar stays authoritative beyond the roster page size. */
export type JobCampaignCounts = {
    total: number;
    byStage: Partial<Record<ProspectStage | 'unknown', number>>;
};

/** A client-parsed CSV row for the outreach import (§2C). All fields optional —
 *  a row may carry only a name/company (a No-contact prospect to enrich later). */
export type CsvImportRow = {
    name?: string;
    email?: string;
    linkedinUrl?: string;
    company?: string;
    title?: string;
    phone?: string;
};

/** `GET /campaigns/by-job/:jobId` — THE campaign the job workspace anchors on
 *  (deterministic server-side selection). `campaign`/`counts` null until one is
 *  provisioned; `addedCandidateKeys` is the set of sourced candidateKeys already
 *  intaken into the campaign (drives the Sourcing view's Add/Added state). */
export type JobCampaignState = {
    campaign: OutreachCampaign | null;
    counts: JobCampaignCounts | null;
    addedCandidateKeys?: string[];
    /** candidateKey → send-target email for the intaken prospects that
     *  enrichment resolved. The only path from a sourced row to a contact
     *  (job-search snapshots carry no emails) — feeds the Sourced CSV export.
     *  Keys absent from the map simply have no email yet. */
    emailByCandidateKey?: Record<string, string>;
};

/** PATCH /prospects/:id/identity — edit a prospect's denormalized identity in
 *  place (Stage 2). Provided keys REPLACE that field wholesale; array order is
 *  preserved and meaningful (emails[0] is the send target, phones[0] the
 *  preferred phone), so callers send the FULL reconstructed array. The server
 *  recomputes identityHashes; 400 = the edit would leave zero identity anchors,
 *  409 = a value already belongs to another prospect in this campaign. */
export type ProspectIdentityPatch = {
    name?: string;
    title?: string;
    company?: string;
    location?: string;
    emails?: string[];
    phones?: string[];
    linkedinUrl?: string;
    websiteUrl?: string;
};

export type EnrollCandidatesPayload = {
    candidateIds?: string[];
    /** This role's applicants — each lazily backed by a Person on enroll (§4). */
    applicationIds?: string[];
    allowDoubleEnroll?: boolean;
};

/** One applicant row for the "Applied to this role" audience source (§4). */
export type JobApplicant = {
    applicationId: string;
    personId: string | null;
    name: string;
    email: string | null;
    linkedinUrl: string | null;
    phone: string | null;
    title: string;
    company: string;
    stage: string;
    source: string;
    hasContact: boolean;
    createdAt: string | null;
};

/* ─── Suppression (§3, §12) ──────────────────────────────── */

export type SuppressionScope = 'global' | 'job' | 'campaign';
export type SuppressionSource = 'unsubscribe' | 'bounce' | 'manual' | 'not_interested' | 'compliance' | 'other';

export type OutreachSuppression = {
    _id: string;
    organizationId: string;
    scope: SuppressionScope;
    jobId: string | null;
    campaignId: string | null;
    candidateId: string | null;
    email: string | null;
    linkedinUrl: string | null;
    reason: string;
    source: SuppressionSource;
    createdBy: string | null;
    createdAt: string;
    updatedAt: string;
};

export type AddSuppressionPayload = {
    scope?: SuppressionScope;
    email?: string;
    linkedinUrl?: string;
    candidateId?: string;
    jobId?: string;
    campaignId?: string;
    reason?: string;
    source?: SuppressionSource;
};

export type ListSuppressionsParams = {
    scope?: SuppressionScope;
    search?: string;
};

/* ─── Launch readiness (§17) ─────────────────────────────── */

/** One blocker/warning the backend returns with a stable `code` + copy. */
export type ReadinessItem = { code: string; message: string };

export type LaunchReadinessStats = {
    enrolled: number;
    /** Ready pool rows launch will auto-enroll (optional — older servers omit it). */
    readyToEnroll?: number;
    /** Total that will enter the flow on launch = enrolled + readyToEnroll. */
    willEnter?: number;
    connectedAccounts: number;
    sendNodes: number;
    noChannel: number;
    missingEmail: number;
    missingLinkedin: number;
    conflicts: number;
};

export type LaunchReadiness = {
    ready: boolean;
    blockers: ReadinessItem[];
    warnings: ReadinessItem[];
    stats?: LaunchReadinessStats;
};

/* ─── Analytics (§13) — campaign / channel / step / job / account (P12) ─ */

export type CampaignAnalyticsSummary = {
    enrolled: number;
    contactable: number;
    active: number;
    paused: number;
    stopped: number;
    completed: number;
    failedEnrollments: number;
    sent: number;
    failed: number;
    replies: number;
    bounces: number;
    unsubscribes: number;
    booked: number;
    replyRate: number;
    bounceRate: number;
    unsubscribeRate: number;
    positiveReplies: number;
    maybeReplies: number;
    negativeReplies: number;
    positiveRate: number;
    /** Mean first-reply latency in ms (0 when no replies yet). */
    avgTimeToReplyMs: number;
    cvRequested: number;
    cvReceived: number;
    movedToPipeline: number;
};

export type StepAnalytics = {
    nodeId: string;
    type: StepType;
    channel: StepChannel | null;
    label: string;
    reached: number;
    sent: number;
    skipped: number;
    failed: number;
    /** Replies that arrived after this node fired (attributed to the driving step). */
    repliesAfter: number;
    positiveAfter: number;
    /** Share of enrollments reaching this node that then stopped. */
    stopRate: number;
    /** Share of reached enrollments that skipped this node onto its fallback. */
    fallbackRate: number;
};

/** Per-channel breakdown (§13 channel: email / linkedin). */
export type ChannelAnalytics = {
    channel: StepChannel;
    sent: number;
    failed: number;
    bounces: number;
    replies: number;
    positiveReplies: number;
    maybeReplies: number;
    negativeReplies: number;
    replyRate: number;
    positiveRate: number;
    avgTimeToReplyMs: number;
    reached: number;
    skipped: number;
    skipRate: number;
};

export type CampaignAnalytics = {
    campaign: CampaignAnalyticsSummary;
    steps: StepAnalytics[];
    channels: ChannelAnalytics[];
};

/* ─── Job responsiveness (§13) ─────────────────────────────── */

export type JobResponsivenessSummary = {
    enrolled: number;
    contactable: number;
    sent: number;
    failed: number;
    replies: number;
    positiveReplies: number;
    maybeReplies: number;
    negativeReplies: number;
    booked: number;
    replyRate: number;
    positiveRate: number;
    avgTimeToReplyMs: number;
};

/** One campaign's headline numbers in the job's campaign-comparison table. */
export type JobCampaignRow = {
    _id: string;
    name: string;
    goal: CampaignGoal;
    status: CampaignStatus;
    enrolled: number;
    sent: number;
    replies: number;
    positiveReplies: number;
    booked: number;
    replyRate: number;
    positiveRate: number;
};

/** A best-responding segment — candidates grouped by seniority or source (§13). */
export type JobSegment = {
    key: string;
    label: string;
    dimension: 'seniority' | 'source';
    enrolled: number;
    contacted: number;
    replies: number;
    positiveReplies: number;
    replyRate: number;
    positiveRate: number;
};

export type JobAnalytics = {
    job: { _id: string; title: string };
    summary: JobResponsivenessSummary;
    campaigns: JobCampaignRow[];
    channels: ChannelAnalytics[];
    segments: JobSegment[];
    bestChannel: StepChannel | null;
    bestSegment: JobSegment | null;
};

/* ─── Org-wide overview (§13) — the standalone Analytics page ─── */

/** One role's headline outreach numbers in the org overview's ranked job list. */
export type OutreachOverviewJobRow = {
    job: { _id: string; title: string };
    summary: JobResponsivenessSummary;
    campaignCount: number;
    activeCampaignCount: number;
};

/** Account-health tally for the overview strip (§13 account, org-level). */
export type OutreachAccountsHealth = {
    total: number;
    healthy: number;
    warming: number;
    limited: number;
    disconnected: number;
};

export type OutreachOverview = {
    summary: JobResponsivenessSummary;
    channels: ChannelAnalytics[];
    bestChannel: StepChannel | null;
    jobs: OutreachOverviewJobRow[];
    accounts: OutreachAccountsHealth;
    totals: { jobs: number; campaigns: number; activeCampaigns: number };
};

/* ─── Account analytics (§13 account) ──────────────────────── */

export type AccountHealth = {
    status: AccountStatus;
    throttled: number;
    errors: number;
    disconnects: number;
    lastActivityAt: string | null;
    lastError: string | null;
};

export type AccountAnalytics = {
    account: {
        _id: string;
        displayName: string;
        emailAddress: string;
        provider: AccountProvider;
        channel: AccountChannel;
        status: AccountStatus;
        lastActivityAt: string | null;
    };
    usage: AccountUsage;
    health: AccountHealth;
};

/* ─── CRM inbox (§9) ─────────────────────────────────────── */

export type ConversationStatus =
    | 'new'
    | 'contacted'
    | 'opened'
    | 'replied'
    | 'interested'
    | 'maybe'
    | 'not_interested'
    | 'meeting_booked'
    | 'cv_requested'
    | 'cv_received'
    | 'moved_to_pipeline'
    | 'archived'
    | 'suppressed';

export type ReplyClass =
    | 'interested'
    | 'maybe'
    | 'not_interested'
    | 'unsubscribe'
    | 'out_of_office'
    | 'wrong_person'
    | 'asks_job_details'
    | 'asks_compensation'
    | 'asks_remote'
    | 'asks_visa'
    | 'asks_schedule'
    | 'already_applied'
    | 'referred_someone'
    | 'unclear'
    | 'negative_escalate';

/** The named quick-filter chips above the inbox list (§9). `needs_action` =
 *  unanswered replies + escalations — everything waiting on a human (snoozed
 *  threads hide until they resurface). `interested` = hot leads; `snoozed` =
 *  parked threads. */
export type InboxFilter = 'needs_action' | 'interested' | 'snoozed' | 'all';

/** Interest rollup (§9 triage): AI-derived from the latest reply class, or a
 *  manual recruiter mark (manual wins — the AI never overwrites it). */
export type InterestLevel = 'interested' | 'maybe' | 'not_interested';
export type InterestSource = 'ai' | 'manual';

export type MessageKind = 'message' | 'note';
export type MessageDirection = 'out' | 'in';

/** Suggested next-step chip set by reply classification (§8). `primary` renders in
 *  coral only when it's the recommended next step. */
export type SuggestedAction = {
    code: string;
    label: string;
    primary: boolean;
    classification?: ReplyClass;
};

/** Candidate summary shared by inbox list items + thread context (§9). */
export type InboxCandidate = {
    _id: string;
    name: string;
    firstName: string;
    avatarUrl: string | null;
    title: string;
    company: string;
    location?: string;
    email?: string | null;
    phone?: string | null;
    linkedInUrl?: string | null;
    websiteUrl?: string | null;
    source?: string | null;
    /** Waterfall-enrichment provenance, populated client-side after a "Find email"
     *  run (or by the server once it serializes it). */
    enrichmentStatus?: EnrichmentStatus | null;
    emailVerification?: EmailVerification | null;
};

/* ─── waterfall contact enrichment (talent outreach) ─── */

export type EmailVerificationStatus = 'deliverable' | 'risky' | 'undeliverable' | 'unknown';
export type EnrichmentStatus = 'none' | 'pending' | 'found' | 'not_found' | 'failed';

/** Normalized deliverability verdict for a found email. */
export type EmailVerification = {
    status: EmailVerificationStatus;
    score: number | null;
    provider: string | null;
};

/** Best verified personal / business address (stored distinctly — Stage 3). */
export type EnrichmentEmailSlot = {
    email: string;
    source: string | null;
    verification: EmailVerification | null;
};

/** Result of an on-demand single-person enrich (`enrichmentApi.enrichPerson`). */
export type EnrichmentResult = {
    found: boolean;
    cached: boolean;
    status: EnrichmentStatus;
    /** Provider could not give a definitive answer; retry later without caching a miss. */
    deferred?: boolean;
    /** Send target — personal preferred, business fallback. */
    email: string | null;
    /** Distinct personal / business finds (either may be null). */
    personalEmail?: EnrichmentEmailSlot | null;
    businessEmail?: EnrichmentEmailSlot | null;
    phone: string | null;
    provider: string | null;
    verification: EmailVerification | null;
    reason?: string;
};

/** Result of a prospect-facing enrich (`enrichmentApi.enrichProspect`, Stage 3). */
export type ProspectEnrichmentResult = EnrichmentResult & {
    /** Found runs only. `duplicate_of_sibling` = the found email already belongs
     *  to ANOTHER prospect in this campaign (append skipped) — this row still
     *  has no send target. */
    appended?: 'appended' | 'already_present' | 'duplicate_of_sibling';
    /** True when this call flipped a HELD row to active (found email made it
     *  reachable). Active/sourced rows never move — this stays false. */
    enrolled?: boolean;
    /** The prospect's stage after this call ('held' | 'active' | …). */
    stage?: ProspectStage;
    /** enrichment_attempts ledger row id. */
    attemptId?: string | null;
    /** Fresh identity snapshot for in-place row updates. */
    identity?: { emails: string[]; phones: string[] };
};

/** Progress of a bulk "enrich missing emails" job (`enrichmentApi.bulkStatus`). */
export type EnrichmentProviderIssue = {
    provider: string;
    kind: string;
    providerCode: string | null;
    retryable: boolean;
    count: number;
};

export type EnrichmentJobStatus = {
    jobId: string;
    status: 'queued' | 'running' | 'waiting' | 'partial' | 'cancelled' | 'completed' | 'failed';
    total: number;
    done: number;
    remaining: number;
    deferred: number;
    found: number;
    notFound: number;
    failed: number;
    skipped: number;
    retryAt: string | null;
    warning: string | null;
    providerIssues: EnrichmentProviderIssue[];
    error: string | null;
};

export type CampaignEmailEnrichmentSummary = {
    eligible: number;
    operation: EnrichmentJobStatus | null;
};

/* ─── ICP sourcing (the outreach kickoff front-half) ─── */

/** Read-only ICP summary for the sourcing kickoff card (`sourcingApi.icpSummary`). */
export type SourcingIcpSummary = {
    hasIcp: boolean;
    roleTitle: string | null;
    location: string | null;
    summary: string | null;
    seniority: string | null;
    experience: string | null;
    mustHaves: string[];
    niceToHaves: string[];
    industry: string[];
    /** The exact Exa people-search string we'll run — shown for transparency. */
    query: string;
};

/** One sourced LinkedIn candidate (preview rows + live run rows). */
export type SourcingCandidate = {
    /** Talent-pool Person id — null until the run imports them. */
    personId: string | null;
    name: string;
    title: string;
    company: string;
    location: string;
    linkedinUrl: string;
    image: string | null;
    email: string | null;
    /** Per-row enrichment lifecycle the live table animates. `skipped` = held
     *  back before enrich (already in this campaign, or do-not-contact). */
    enrichState: 'pending' | 'finding' | 'found' | 'not_found' | 'skipped';
    /** Why a `skipped` row was held back: 'already_enrolled' | 'suppressed'. */
    skipReason?: string | null;
    verification: EmailVerification | null;
    enrolled: boolean;
    /** True when the import matched an existing Person (deduped, not created). */
    existing: boolean;
};

/** Result of `sourcingApi.preview` — candidates without saving + a spend estimate. */
export type SourcingPreview = {
    jobId: string;
    query: string;
    total: number;
    /** How many the recruiter asked for (echoed back so the UI can flag a shortfall). */
    requested?: number;
    /** `requested - total` when Exa couldn't supply the full ask even after varying
     *  the query — drives the "found N of M; widen the ICP" note. 0 when filled. */
    shortfall?: number;
    candidates: SourcingCandidate[];
    /** Human note when off-ICP rows were filtered out (region/experience). */
    filterNote?: string | null;
    /** How many semantic-but-off-ICP rows were dropped from the preview. */
    droppedOffIcp?: number;
    wouldSpendCredits: {
        finder: number;
        verifier: number;
        total: number;
        note: string;
    };
};

export type SourcingPhase = 'sourcing' | 'enriching' | 'enrolling' | 'ready';

/** Live phased progress of a sourcing run (`sourcingApi.jobStatus`). */
export type SourcingJobStatus = {
    jobId: string;
    campaignId: string;
    phase: SourcingPhase;
    status: 'queued' | 'running' | 'completed' | 'failed';
    query: string;
    limit: number;
    total: number;
    sourced: number;
    found: number;
    verified: number;
    enrolled: number;
    /** Enrolled candidates with a clean (deliverable) verdict — the rest are
     *  risky and may bounce. Drives the "N enrolled · M deliverable" header. */
    deliverable: number;
    /** Rows held back before enrich (already in this campaign, or do-not-contact). */
    skipped: number;
    failed: number;
    error: string | null;
    candidates: SourcingCandidate[];
};

export type InboxCampaignTag = {
    _id: string;
    name: string;
    goal?: CampaignGoal;
    status?: CampaignStatus;
    jobId?: string;
    /** Populated on the thread view only (getThread) — drives the composer's
     *  booking-link insert; absent on the inbox list tags. */
    bookingLink?: string;
};

export type InboxPreview = {
    body: string;
    subject?: string;
    direction: MessageDirection | null;
    channel: StepChannel | null;
    provider?: AccountProvider | null;
    kind: MessageKind;
};

/** One row in the inbox list (§9 list item). */
export type InboxItem = {
    _id: string;
    candidateId: string;
    candidate: InboxCandidate | null;
    status: ConversationStatus;
    latestClassification: ReplyClass | null;
    needsReply: boolean;
    /** Latest inbound is a risky/sensitive class — human only (§8). */
    escalated: boolean;
    suggestedAction: SuggestedAction | null;
    interestLevel: InterestLevel | null;
    interestSource: InterestSource | null;
    snoozedUntil: string | null;
    channels: StepChannel[];
    jobIds: string[];
    campaigns: InboxCampaignTag[];
    ownerId: string | null;
    assignedUserId: string | null;
    lastActivityAt: string;
    lastInboundAt: string | null;
    lastOutboundAt: string | null;
    preview: InboxPreview | null;
};

export type InboxResponse = {
    items: InboxItem[];
    total: number;
    limit: number;
    skip: number;
    /** Opaque stable-sort cursor used for this page (null on the first page). */
    cursor: string | null;
    /** Cursor for the next page; null when this is the final page. */
    nextCursor: string | null;
    hasMore: boolean;
    /** Opaque version of the filtered feed used to prevent cross-generation page merges. */
    feedVersion: string;
};

export type InboxParams = {
    filter?: InboxFilter;
    channel?: StepChannel;
    campaignId?: string;
    jobId?: string;
    ownerId?: string;
    search?: string;
    limit?: number;
    skip?: number;
    cursor?: string;
};

/** An inbound reply that matched no candidate (status:'unmatched' event) — the
 *  triage shape for the inbox "Unmatched replies" surface (hardening). */
export type UnmatchedEvent = {
    _id: string;
    channel: StepChannel | null;
    /** Best-effort sender id: email / LinkedIn URL / phone / provider id. */
    sender: string | null;
    snippet: string;
    receivedAt: string;
    /** null = the account never resolved to an org (admin-only orphan). */
    organizationId: string | null;
    providerEventId: string;
};

export type UnmatchedEventsResponse = {
    events: UnmatchedEvent[];
    /** Whether null-org orphans were folded in (admin + ?includeUnscoped=true). */
    includeUnscoped: boolean;
};

/** A single entry in the unified cross-channel timeline (§9). */
export type ThreadMessageAccount = {
    _id: string;
    displayName?: string;
    emailAddress?: string;
    provider?: AccountProvider;
    channel?: AccountChannel;
};

export type ThreadMessage = {
    _id: string;
    conversationId: string;
    candidateId: string;
    kind: MessageKind;
    channel: StepChannel | null;
    linkedinAction?: 'invite' | 'message' | 'inmail' | null;
    accountId: ThreadMessageAccount | string | null;
    direction: MessageDirection | null;
    subject: string;
    body: string;
    aiGenerated: boolean;
    aiMode: AiMode | null;
    classification: ReplyClass | null;
    status: string;
    authoredBy: string | null;
    sentAt: string | null;
    receivedAt: string | null;
    createdAt: string;
};

export type ThreadEnrollment = {
    _id: string;
    campaignId: string;
    status: EnrollmentStatus;
    currentNodeId: string | null;
    nextRunAt: string | null;
    stopReason: StopReason | null;
    sequenceVersion: number;
};

export type Conversation = {
    _id: string;
    organizationId: string;
    candidateId: string;
    jobIds: string[];
    campaignIds: string[];
    ownerId: string | null;
    assignedUserId: string | null;
    status: ConversationStatus;
    latestClassification: ReplyClass | null;
    needsReply: boolean;
    escalated: boolean;
    escalationReason: string | null;
    suggestedAction: SuggestedAction | null;
    interestLevel: InterestLevel | null;
    interestSource: InterestSource | null;
    snoozedUntil: string | null;
    channels: StepChannel[];
    providerThreadIds: { email: string | null; linkedin: string | null; whatsapp: string | null };
    lastActivityAt: string;
    lastInboundAt: string | null;
    lastOutboundAt: string | null;
    createdAt: string;
    updatedAt: string;
};

/** Which account each channel's reply will go out on (§9 composer pin). */
export type ReplyAccounts = Partial<
    Record<
        StepChannel,
        {
            accountId: string;
            displayName?: string | null;
            emailAddress?: string | null;
            provider?: AccountProvider | null;
            channel: StepChannel;
            /** Current health of the sender for this channel — the composer disables
             *  + prompts reconnect when it isn't connected/warming (Stage 1). */
            status?: AccountStatus | null;
        }
    >
>;

export type ThreadResponse = {
    conversation: Conversation;
    candidate: InboxCandidate | null;
    messages: ThreadMessage[];
    enrollments: ThreadEnrollment[];
    campaigns: InboxCampaignTag[];
    replyAccounts: ReplyAccounts;
};

/** One entry in the context pane's activity feed — a trimmed OutreachEvent
 *  (send / reply / bounce / connection update) for the conversation (§9). */
export type ThreadActivityEntry = {
    _id: string;
    type:
        | 'message.received'
        | 'message.sent'
        | 'message.status'
        | 'email.bounce'
        | 'email.failed'
        | 'relation.update'
        | 'account.status';
    channel: StepChannel | null;
    at: string;
    /** True when the send was a recruiter's manual inbox reply (not the engine). */
    manual: boolean;
};

export type ThreadActivityResponse = {
    events: ThreadActivityEntry[];
    /** Conversation createdAt — closes the feed with a "started" entry. */
    startedAt: string;
};

export type ReplyPayload = {
    channel?: StepChannel;
    body: string;
    subject?: string;
    /** Caps escape hatch (H12): the recruiter confirmed "account at its safety
     *  cap — send anyway?". Composer one-offs only. */
    override?: boolean;
};

export type ReplyResult = {
    message: ThreadMessage;
    stopped: number;
};

/** Request an AI-suggested reply draft for a thread (returns copy only, never
 *  sends). The channel defaults to the thread's primary channel server-side. */
export type DraftReplyPayload = {
    channel?: StepChannel;
};

/** An AI-suggested reply the recruiter reviews + edits before sending. `source`
 *  is `'fallback'` when no model was available (deterministic copy). */
export type DraftReplyResult = {
    channel: StepChannel;
    classification: ReplyClass | null;
    body: string;
    source: 'ai' | 'fallback';
};

/* ─── Notifications (§9) ─────────────────────────────────── */

/** In-app notification counts for the inbox/nav badge (§9). `needsAction` =
 *  unanswered replies + escalations (matches the inbox's default filter). */
export type NotificationSummary = {
    needsAction: number;
    escalated: number;
    /** Inbound replies that matched no candidate in the last 24h (H3 triage). */
    unmatchedLast24h: number;
    /** Disconnected/error sender accounts — outreach bound to them is stalled. */
    accountsDown: number;
};

/* ─── Ops health (H11) ────────────────────────────────────── */

/** One background loop's heartbeat (engine tick / inbound drain). */
export type OpsLoopHealth = {
    lastTickAt: string | null;
    lastCounts: Record<string, number> | null;
    lastErrorAt: string | null;
    lastError: string | null;
    expectedCadenceMs: number;
    /** No heartbeat for 5× the expected cadence (or never ran). */
    stale: boolean;
};

/** GET /outreach/ops/health — the hub shows a warning banner when not healthy. */
export type OpsHealth = {
    engine: OpsLoopHealth;
    inbound: OpsLoopHealth;
    healthy: boolean;
};

/* ─── Message authoring: preview + AI drafts + templates (§5, §7) ─ */

/** The §7 variable palette. `unsubscribe.link` is system-injected (never missing). */
export const MESSAGE_VARIABLES = [
    'candidate.firstName',
    'candidate.fullName',
    'candidate.currentTitle',
    'candidate.currentCompany',
    'job.title',
    'company.name',
    'recruiter.firstName',
    'recruiter.fullName',
    'calendar.link',
    'unsubscribe.link'
] as const;

export type MessageVariable = (typeof MESSAGE_VARIABLES)[number];

/** Recruiter-friendly labels for the §7 variable palette — shown in the chip
 *  picker and the preview, while the raw `{{token}}` is what gets spliced into
 *  copy and resolved server-side. */
export const MESSAGE_VARIABLE_LABELS: Record<MessageVariable, string> = {
    'candidate.firstName': 'First name',
    'candidate.fullName': 'Full name',
    'candidate.currentTitle': 'Current title',
    'candidate.currentCompany': 'Current company',
    'job.title': 'Job title',
    'company.name': 'Company',
    'recruiter.firstName': 'Your first name',
    'recruiter.fullName': 'Your full name',
    'calendar.link': 'Calendar link',
    'unsubscribe.link': 'Unsubscribe link'
};

/** template = the node's saved copy; ai = an AI draft for this candidate (§7). */
export type PreviewMode = 'template' | 'ai';

export type MessagePreviewRequest = {
    nodeId: string;
    candidateId?: string;
    mode?: PreviewMode;
    /** The live (possibly unsaved) node from the editor. Sent so the preview
     *  reflects on-screen edits — the editor never autosaves. Read-only on the
     *  server; falls back to the saved node when omitted. */
    draftNode?: StepNode;
    /** Resolve the sequence version this enrolled prospect will actually send,
     *  rather than the campaign's latest editor version. Used by the person drawer. */
    useProspectSequence?: boolean;
};

export type PrewriteView = {
    source: 'preview' | 'edited';
    /** Stale relative to the prospect's pinned executable step. This controls
     *  send applicability and is intentionally independent of the latest flow. */
    stale: boolean;
    /** Human edit differs from the matching node in the campaign's latest flow
     *  version. Informational only; the prospect remains pinned to its version. */
    staleAgainstLatestTemplate?: boolean;
    usable: boolean;
    generatedAt: string | null;
    editedAt: string | null;
    usedAt: string | null;
};

/** A candidate to preview/approve against (enrolled, else campaign audience). */
export type SampleCandidate = {
    _id: string;
    name: string;
    firstName: string;
    title: string;
    company: string;
    location: string;
    avatarUrl: string | null;
    email: string | null;
    linkedinUrl: string | null;
    source: string | null;
};

/** A resolved per-candidate preview (§5 live preview panel). */
export type MessagePreviewResult = {
    mode: PreviewMode;
    channel: StepChannel | null;
    stepType: StepType;
    aiMode: AiMode;
    candidateId: string | null;
    candidate: SampleCandidate | null;
    accountId: string | null;
    accountLabel: string | null;
    subject: string;
    body: string;
    /** Variables that are unknown or empty for this candidate — highlight these. */
    missingVariables: string[];
    unsubscribeFooter: boolean;
    /** AI-only: which real candidate signal personalized the draft (§7 audit). */
    signalUsed: string | null;
    signalText: string | null;
    /** 'ai' (model) or 'fallback' (no provider configured). */
    draftSource: 'ai' | 'fallback' | null;
    /** Email AI is static: this exact copy is generated once and sent to EVERYONE
     *  (no per-candidate personalization). Drives the "same for everyone" notice. */
    aiStatic?: boolean;
    /** Advanced `{{ai2}}` parts were generated fresh for THIS sample prospect
     *  (per-prospect at send time, sourcing profile included when known). */
    aiPerProspect?: boolean;
    /** Pinned per-prospect copy, when preview persistence succeeded. */
    prewrite?: PrewriteView | null;
};

export type SendTestResult = {
    sent: boolean;
    to: string;
    simulated: boolean;
    accountId: string;
    providerMessageId: string | null;
};

/** Lightweight saved-template summary for the picker (§5). */
export type OutreachTemplateSummary = {
    _id: string;
    templateName: string;
    nodeCount: number;
    sendNodes: number;
    hasBranch: boolean;
    createdAt: string;
    updatedAt: string;
};

/* ─── NL sequence builder (§5, P13) ──────────────────────── *
 * The conversational builder generates + edits the branch tree by chat. The
 * conversation is checkpointed server-side (SequenceBuilderThread) so it resumes;
 * the runnable artifact lives on the Sequence. */

export type BuilderRole = 'user' | 'assistant';
export type BuilderTurnKind = 'chat' | 'questions' | 'generated';

/** A guiding question with optional quick-reply suggestions. The options render as
 *  clickable chips in the chat; the first is the assistant's default answer. */
export type BuilderQuestion = {
    question: string;
    options?: string[];
};

/** One checkpointed turn in the builder conversation. */
export type BuilderMessage = {
    role: BuilderRole;
    content: string;
    kind?: BuilderTurnKind;
    /** Present on an assistant turn that only asked guiding questions. */
    questions?: BuilderQuestion[];
    /** Plain-English plan summary captured on a generating turn. */
    planSummary?: string;
    createdAt?: string;
};

/** Which channels the builder may generate steps for — connected, capable accounts. */
export type BuilderChannels = { email: boolean; linkedin: boolean; whatsapp: boolean };

/** GET /campaigns/:id/sequence/builder — resume the chat + current tree. */
export type BuilderThreadResponse = {
    messages: BuilderMessage[];
    planSummary: string;
    sequence: OutreachSequence | null;
    channels: BuilderChannels;
    goal: CampaignGoal;
};

/* ─── Compliance settings (§12) ──────────────────────────── *
 * Org-level email compliance. Per-account `signature` lives on OutreachAccount;
 * the org postal address + unsubscribe footer are org-wide. No dedicated backend
 * endpoint ships in P1–P4 yet, so the Settings UI persists these client-side as
 * a stopgap (see store) until the compliance API lands. */
export type ComplianceSettings = {
    orgPostalAddress: string;
    unsubscribeFooter: string;
};

/* ─── Reply behavior — admin/agent controls (§4, §8, P13) ── *
 * How the agent handles inbound replies. Two settings: an autonomy mode and
 * per-class escalation rules. `manual` autonomy means the agent never drafts; the
 * recruiter writes every reply. `ai_draft` means low-risk replies are drafted for
 * a human to approve. The §8 "never auto-send" classes are LOCKED to a human and
 * cannot be relaxed. Admin-only writes (`outreach.admin`). */
export type AutonomyMode = 'manual' | 'ai_draft';
export type EscalationRule = 'auto_handle' | 'human';

/** One reply class's effective routing for the settings UI. */
export type ReplyBehaviorClassRule = {
    class: ReplyClass;
    description: string;
    rule: EscalationRule;
    /** §8 locked class — always routes to a human; the toggle is disabled. */
    locked: boolean;
    /** Whether this class could ever be AI-drafted (before autonomy gating). */
    draftable: boolean;
};

export type ReplyBehaviorView = {
    autonomyMode: AutonomyMode;
    rules: ReplyBehaviorClassRule[];
    lockedClasses: ReplyClass[];
    updatedAt: string | null;
    updatedBy: { _id: string; name: string } | null;
};

export type UpdateReplyBehaviorPayload = {
    autonomyMode?: AutonomyMode;
    /** Full replace of the configurable per-class rules (locked classes ignored). */
    escalationRules?: Partial<Record<ReplyClass, EscalationRule>>;
};

/* ─── Audit log (§12, P13) ────────────────────────────────── */
export type AuditEntry = {
    _id: string;
    action: string;
    targetType: string | null;
    targetId: string | null;
    summary: string;
    metadata: Record<string, unknown>;
    actor: { _id: string; name: string } | null;
    createdAt: string;
};

/* ─── Template apply result (§5, P13) ─────────────────────── *
 * Applying a template re-adapts its copy to the campaign's job/ICP while keeping
 * the branch logic; `adaptedNodes` is how many send nodes were re-adapted. */
export type ApplyTemplateResult = {
    sequence: OutreachSequence;
    forked: boolean;
    reAdapted: boolean;
    adaptedNodes: number;
};

/* ───────────────────────── prospect flow progress (person drawer) ───────────────────────── */

/** Per-step status the engine actually produced / will produce for a prospect. */
export type FlowStepStatus =
    | 'sent' // went out (uses the real sent subject/body + sentAt)
    | 'sending' // a message row is queued/in-flight right now
    | 'queued' // the current step, scheduled — sends at estSendAt (nextRunAt)
    | 'upcoming' // a future step not yet reached — estSendAt is an ESTIMATE
    | 'retrying' // a lookup or send failed; the current step will retry
    | 'blocked' // lookup failed; paused until explicitly resumed
    | 'failed' // the send failed and the engine moved on
    | 'bounced' // a hard bounce was recorded → won't reach
    | 'wont_reach' // no address for this step's channel
    | 'skipped' // the engine passed this step (branch/fallback) without sending
    | 'ended'; // the sequence stopped/completed before reaching this step

export type ProspectFlowStep = {
    nodeId: string;
    type: StepType;
    channel: StepChannel | null;
    order: number;
    status: FlowStepStatus;
    reachable: boolean;
    attempts: number;
    sentAt: string | null;
    estSendAt: string | null;
    failureReason: string | null;
    engagement: 'opened' | 'clicked' | null;
    hasMessage: boolean;
    subject: string | null;
    body: string | null;
    hasAdvancedAi?: boolean;
    prewrite?: PrewriteView | null;
};

export type ProspectProgressSummary = {
    status: EnrollmentStatus | null;
    stage: ProspectStage | null;
    stopReason: StopReason | null;
    pauseReason: string | null;
    nextRunAt: string | null;
    pausedUntil: string | null;
    currentNodeId: string | null;
    sentCount: number;
    totalSteps: number;
};

/** Compact flow summary the roster endpoint attaches to each enrolled prospect
 *  row (current stage + step status + estimated next send). Null for non-enrolled.
 *  `status: 'waiting'` = parked on a non-send node (branch condition window) —
 *  stepOrder is null but the prospect is NOT terminal; `estSendAt` is then the
 *  branch's decision deadline and `conditionLabel` its question.
 *  `status: 'not_started'` = enrolled but the engine hasn't begun (pre-launch,
 *  or awaiting the first tick) — never render it as ended. */
export type ProspectRowFlow = {
    stepOrder: number | null;
    totalSteps: number;
    stepType: StepType | 'branch' | null;
    status: FlowStepStatus | 'waiting' | 'not_started' | null;
    estSendAt: string | null;
    sentCount: number;
    conditionLabel: string | null;
};

/** Extra status values a branch/other node can carry (beyond FlowStepStatus). */
export type FlowGraphNodeStatus = FlowStepStatus | 'decided' | 'pending' | 'unreached' | 'done';

/** One node of the branch-tree graph the drawer renders. Send nodes reuse the
 *  ProspectFlowStep fields; branch nodes carry their decided outcome. `onPath`
 *  marks whether THIS person actually reached it (visited / current). */
export type FlowGraphNode = {
    nodeId: string;
    type: StepType | 'branch' | string;
    kind: 'send' | 'branch' | 'other';
    channel: StepChannel | null;
    status: FlowGraphNodeStatus;
    onPath: boolean;
    isCurrent: boolean;
    // branch-only
    branchOutcome?: 'yes' | 'no' | null;
    conditionLabel?: string;
    // send-only (mirrors ProspectFlowStep)
    order?: number;
    reachable?: boolean;
    attempts?: number;
    sentAt?: string | null;
    estSendAt?: string | null;
    failureReason?: string | null;
    engagement?: 'opened' | 'clicked' | null;
    hasMessage?: boolean;
    subject?: string | null;
    body?: string | null;
    hasAdvancedAi?: boolean;
    prewrite?: PrewriteView | null;
};

export type FlowGraphEdge = {
    source: string;
    target: string;
    kind: 'linear' | 'yes' | 'no';
    /** Was this edge actually traversed by this person? */
    taken: boolean;
};

/** The sequence's branch tree + which path this person took — powers the drawer's
 *  tree view (replaces the flattened linear `steps` list for rendering). */
export type FlowGraph = {
    entryNodeId: string | null;
    nodes: FlowGraphNode[];
    edges: FlowGraphEdge[];
};

export type ProspectProgress = {
    prospect: {
        _id: string;
        campaignId: string;
        identity: ProspectIdentity;
        status: EnrollmentStatus | null;
        stage: ProspectStage;
        currentNodeId: string | null;
        nextRunAt: string | null;
        stopReason: StopReason | null;
        pauseReason: string | null;
        pausedUntil: string | null;
        heldUntil: string | null;
        updatedAt: string;
    };
    campaign: { _id: string; name: string; status: CampaignStatus } | null;
    steps: ProspectFlowStep[];
    summary: ProspectProgressSummary;
    /** Branch-tree graph (present when the prospect has a sequence). */
    graph?: FlowGraph | null;
};
